import { motion } from 'framer-motion'
import { MessageSquare, Zap, Settings, Layout } from 'lucide-react'

const BasicUsage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Layout className="mr-3 text-accent-cyan" size={32} />
        基本使用
      </h1>

      {/* 界面介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">界面概览</h2>
        <p className="text-gray-400 mb-4">
          Avelia 采用简洁直观的界面设计，主要分为以下几个区域：
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5">
            <MessageSquare className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">对话区域</h3>
            <p className="text-sm text-gray-400">
              显示与 AI 的对话历史，支持文字、图片、文件等多种消息类型。可以滚动查看历史对话。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <Zap className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">输入框</h3>
            <p className="text-sm text-gray-400">
              底部输入框用于输入消息，支持多行输入、文件拖拽、快捷键等功能。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <Settings className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">侧边栏</h3>
            <p className="text-sm text-gray-400">
              左侧显示会话列表，可以切换不同会话、创建新会话、管理会话历史。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <Layout className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">工具面板</h3>
            <p className="text-sm text-gray-400">
              右侧显示当前可用的工具和功能，包括知识库、记忆管理、设置等。
            </p>
          </div>
        </div>
      </section>

      {/* 基本操作 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">基本操作</h2>
        
        <div className="space-y-6">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">💬 发送消息</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>在输入框中输入消息，按 <kbd className="px-2 py-1 bg-black/30 rounded text-xs">Enter</kbd> 发送</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>使用 <kbd className="px-2 py-1 bg-black/30 rounded text-xs">Shift + Enter</kbd> 换行</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>支持 Markdown 格式，可以使用 <code className="px-2 py-1 bg-black/30 rounded">**粗体**</code>、<code className="px-2 py-1 bg-black/30 rounded">*斜体*</code> 等</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">📎 附加文件</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>点击输入框左侧的附件按钮选择文件</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>直接拖拽文件到输入框</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>支持的文件类型：文档（PDF、Word、TXT）、图片（JPG、PNG）、代码文件等</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🔄 管理会话</h3>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">新建会话：</strong>点击左上角的 "+" 按钮创建新会话</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">切换会话：</strong>在侧边栏点击任意会话进行切换</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">删除会话：</strong>右键点击会话，选择"删除"</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">重命名会话：</strong>右键点击会话，选择"重命名"</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 两种模式 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">对话模式</h2>
        <p className="text-gray-400 mb-4">
          Avelia 支持两种对话模式，适用于不同场景：
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5 border-l-4 border-blue-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <MessageSquare className="mr-2 text-blue-400" size={24} />
              聊天模式
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              自然对话，获取信息、回答问题、提供建议。
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <p>✓ 快速响应</p>
              <p>✓ 适合简单查询</p>
              <p>✓ 不调用工具</p>
            </div>
            <div className="mt-3 p-3 bg-blue-500/10 rounded text-xs">
              <strong className="text-blue-300">示例：</strong>
              <p className="text-gray-400 mt-1">"今天天气怎么样？"</p>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5 border-l-4 border-purple-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Zap className="mr-2 text-purple-400" size={24} />
              任务模式
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              自动调用工具，执行复杂任务，完成实际操作。
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <p>✓ 自动选择工具</p>
              <p>✓ 执行多步操作</p>
              <p>✓ 返回执行结果</p>
            </div>
            <div className="mt-3 p-3 bg-purple-500/10 rounded text-xs">
              <strong className="text-purple-300">示例：</strong>
              <p className="text-gray-400 mt-1">"帮我整理桌面上的文件"</p>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 glass-effect rounded-lg border border-accent-cyan/30">
          <p className="text-sm text-gray-400">
            <strong className="text-accent-cyan">💡 提示：</strong>
            Avelia 会根据你的需求自动选择合适的模式。如果需要明确指定，可以在输入框右侧切换模式。
          </p>
        </div>
      </section>

      {/* 快捷键 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">常用快捷键</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">功能</th>
                <th className="text-left p-3 font-semibold">快捷键</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3">发送消息</td>
                <td className="p-3"><kbd className="px-2 py-1 bg-black/30 rounded text-xs">Enter</kbd></td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">换行</td>
                <td className="p-3"><kbd className="px-2 py-1 bg-black/30 rounded text-xs">Shift + Enter</kbd></td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">新建会话</td>
                <td className="p-3"><kbd className="px-2 py-1 bg-black/30 rounded text-xs">Ctrl + N</kbd></td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">切换到搜索</td>
                <td className="p-3"><kbd className="px-2 py-1 bg-black/30 rounded text-xs">Ctrl + K</kbd></td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">打开设置</td>
                <td className="p-3"><kbd className="px-2 py-1 bg-black/30 rounded text-xs">Ctrl + ,</kbd></td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3">清空当前会话</td>
                <td className="p-3"><kbd className="px-2 py-1 bg-black/30 rounded text-xs">Ctrl + L</kbd></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 提示 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-green-500/30">
        <h3 className="text-xl font-semibold mb-3 flex items-center">
          <Zap className="mr-2 text-green-400" size={24} />
          使用技巧
        </h3>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">明确描述需求：</strong>越详细的描述，AI 能给出越准确的回答</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">分步骤提问：</strong>复杂任务可以拆分成多个小问题</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">提供上下文：</strong>引用之前的对话内容，AI 能更好理解你的意图</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">善用知识库：</strong>导入文档后，AI 可以基于你的资料回答问题</span>
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default BasicUsage
